define('viewModels/privileged/base/BaseTemplateEditor',[
    'lib/knockout',
    'services/navHelper',
    'util/errorCallbackFactory',
    'viewModels/ViewModel',
    'models/config/SchemaModel',
    'viewModels/privileged/devices/SchemaForm',
    'util/SchemaPreprocessor'
], function (ko, navHelper, errorCallbackFactory, ViewModel, SchemaModel, SchemaForm, SchemaPreprocessor) {

    'use strict';

    var BaseTemplateEditor = ViewModel.extend(function () {
        var self = this;

        /**
         * This will be true if this is a new entity.
         */
        self.isNew = ko.observable(true);

        /**
         * A reference to the editor setup.
         */
        self.setup = ko.observable(null);

        /**
         * The type of template entity that is being configured.
         */
        self.configEntityType = ko.observable(null);

        /**
         * The config service to use for the config entity type.
         */
        self.configService = null;

        /**
         * The SchemaModel for the editor.
         */
        self.schemaModel = ko.observable(null);

        /**
         * The SchemaForm for the simple schema options.
         */
        self.simpleSchemaForm = ko.observable(null);

        /**
         * The SchemaForm for the advanced schema options.
         */
        self.advancedSchemaForm = ko.observable(null);

        /**
         * The schema that the template is based on.
         */
        self.schema = null;

        /**
         * The loading state for the schema data.
         */
        self.loadingSchema = ko.observable(false);

        /**
         * The template that is being edited.
         */
        self.template = null;

        /**
         * The loading state for the template data.
         */
        self.loadingTemplate = ko.observable(false);

        /**
         * The loading state for the save button.
         */
        self.loadingSaveButton = ko.observable(false);

        /**
         * Loads the schema.
         */
        self.loadSchema = function (schemaUri) {
            var self = this;
            self.loadingSchema(true);
            return self.configService.readSchema(schemaUri)
                .then(function (response) {
                    response.id = schemaUri; // This will happen automatically when implemented in public API.
                    self.schema = response;
                    return response;
                })
                .finally(function () {
                    self.loadingSchema(false);
                });
        };

        /**
         * Loads a template.
         */
        self.loadTemplate = function (templateUri) {
            var self = this;
            self.loadingTemplate(true);
            return self.configService.readTemplate(templateUri)
                .then(function (response) {
                    self.template = response;
                    return response;
                })
                .finally(function () {
                    self.loadingTemplate(false);
                });
        };

        self.loadBehavior = function (behaviorName) {
            return self.configService.readBehavior(self.schema.id, behaviorName)
                .then(function (response) {
                    return response;
                });
        };

        var configureSchemaForms = function (namespace, schema, data) {
            self.loadBehavior('template')
                .then(function (behavior) {
                    var filter = new SchemaPreprocessor(schema);
                    var templateSchema = filter.inclusionBehavior(behavior.template);
                    var model = new SchemaModel(templateSchema, data);
                    self.schemaModel(model);
                    self.loadBehavior('simple')
                        .then(function(behavior) {
                            var filter = new SchemaPreprocessor(templateSchema);
                            self.simpleSchemaForm(self.subView(SchemaForm, namespace, model, filter.inclusionBehavior(behavior.simple)));
                            self.advancedSchemaForm(self.subView(SchemaForm, namespace, model, filter.exclusionBehavior(behavior.simple)));
                        });

                }).catch(errorCallbackFactory.createCallback({ namespace: 'config.error' }));
        };

        /**
         * Configures to create template from only a schema.
         */
        self.configureForCreate = function (namespace, schemaUri) {
            self.loadSchema(schemaUri)
                .then(function (schema) {
                    configureSchemaForms(namespace, schema);
                })
                .catch(errorCallbackFactory.createCallback({ namespace: 'config.error' }));
        };

        /**
         * Configures to update an existing template.
         */
        self.configureForUpdate = function (namespace, templateUri) {
            self.isNew(false);
            var loadedResponse;
            self.loadTemplate(templateUri)
                .then(function (templateResponse) {
                    loadedResponse = templateResponse;
                    return self.loadSchema(templateResponse.schema.selfUri);
                })
                .then(function (schema) {
                    configureSchemaForms(namespace, schema, loadedResponse.properties);
                })
                .catch(errorCallbackFactory.createCallback({ namespace: 'config.error' }));
        };

        /**
         * Configures this ViewModel.
         */
        self.configure = function (options) {
            options = options ? options : {};

            self.isNew(true);
            self.setup(options.setup);

            // Configure for the correct entity context.
            if (options.templateUri) {
                self.configureForUpdate(options.namespace, options.templateUri);
            }
            else if (options.schemaUri) {
                self.configureForCreate(options.namespace, options.schemaUri);
            }
            else {
                throw "Invalid initialization options in BaseTemplateEditor.configure()";
            }
        };

        /**
         * The current validity state of the SchemaForm data.
         */
        self.isValid = self.computed(function () {
            if (self.setup() && self.schemaModel()) {
                return self.setup().isValid() &&
                    self.schemaModel().isValid();
            }
            return false;
        });

        /**
         * Creates a new entity from the SchemaForm data.
         */
        self.getCreatePayload = function () {
            return {
                properties: self.schemaModel().unwrap(),
                schema: {
                    selfUri: self.schema.id
                }
            };
        };

        /**
         * Updates the SchemaForm entity with new data.
         */
        self.getUpdatePayload = function () {
            return {
                properties: self.schemaModel().unwrap(),
                schema: {
                    selfUri: self.schema.id
                }
            };
        };

        /**
         * Performs a contextually sensitive operation to save the entity.
         */
        self.save = function () {
            if (self.isValid()) {
                var deferred;
                self.loadingSaveButton(true);
                if (self.isNew()) {
                    deferred = self.configService.createTemplate(this.getCreatePayload());
                }
                else {
                    deferred = self.configService.updateTemplate(self.template.selfUri, this.getUpdatePayload());
                }
                var context = this;
                deferred
                    .then(function () {
                        navHelper.navigateTo(context.cancelHref());
                    })
                    .catch(errorCallbackFactory.createCallback({namespace: 'config.error' }))
                    .finally(function () {
                        self.loadingSaveButton(false);
                    });
            }
        };

        /**
         * Return the user to the prior page.
         */
        self.cancelHref = self.computed(function () {
            return '#';
        });
    });

    return BaseTemplateEditor;
});

